import React, { useEffect, useMemo, useState } from "react";
import {
  BookOpen,
  Bug,
  Check,
  CircleAlert,
  Flag,
  GitBranch,
  MapPin,
  RotateCcw,
  Users,
  X,
} from "lucide-react";
import { Button, IconButton } from "../ui/Button";
import {
  IMPACT_TYPES,
  advancePreview,
  getAvailableAnswers,
  startPreview,
} from "./storyModel";

const ENDING_LABELS = {
  true: "真结局",
  good: "好结局",
  normal: "普通结局",
  bad: "坏结局",
  hidden: "隐藏结局",
};

const nodeById = (story, nodeId) => story.nodes.find((node) => node.id === nodeId) || null;

function RuntimeDebug({ story, session }) {
  const characterName = (id) => story.characters.find((character) => character.id === id)?.name || id;
  const relationEntries = Object.entries(session.runtime?.relations || {});
  const flagEntries = Object.entries(session.runtime?.flags || {});
  const stageEntries = Object.entries(session.runtime?.stages || {});

  return (
    <aside className="interactive-preview__debug" aria-label="预演状态调试">
      <div className="interactive-preview__debug-section">
        <strong>人物关系</strong>
        {relationEntries.length ? relationEntries.map(([key, value]) => (
          <span key={key}>{characterName(key)} <b>{String(value)}</b></span>
        )) : <span className="is-empty">暂无变化</span>}
      </div>
      <div className="interactive-preview__debug-section">
        <strong>人物阶段</strong>
        {stageEntries.length ? stageEntries.map(([key, value]) => (
          <span key={key}>{characterName(key)} <b>{String(value)}</b></span>
        )) : <span className="is-empty">暂无变化</span>}
      </div>
      <div className="interactive-preview__debug-section">
        <strong>剧情标记</strong>
        {flagEntries.length ? flagEntries.map(([key, value]) => (
          <span key={key}>{key} <b>{String(value)}</b></span>
        )) : <span className="is-empty">暂无变化</span>}
      </div>
      <div className="interactive-preview__debug-section">
        <strong>选择记录</strong>
        {session.history.length ? session.history.map((item, index) => (
          <span key={`${item.nodeId}-${item.answerId}-${index}`}>{index + 1}. {item.answerText}</span>
        )) : <span className="is-empty">还没有做出选择</span>}
      </div>
    </aside>
  );
}

export default function StoryPreviewModal({ story, startNodeId, onClose }) {
  const [session, setSession] = useState(() => startPreview(story, { startNodeId }));
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    setSession(startPreview(story, { startNodeId }));
  }, [story, startNodeId]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const currentNode = useMemo(
    () => nodeById(story, session.currentNodeId) || session.ending || null,
    [story, session.currentNodeId, session.ending],
  );
  const chapter = story.chapters.find((item) => item.id === currentNode?.chapterId);
  const participants = (currentNode?.participants || [])
    .map((id) => story.characters.find((character) => character.id === id))
    .filter(Boolean);
  const availableAnswers = currentNode?.nodeType === "story"
    ? getAvailableAnswers(currentNode, session.runtime)
    : [];
  const hiddenAnswerCount = Math.max(0, (currentNode?.answers?.length || 0) - availableAnswers.length);

  const restart = () => setSession(startPreview(story, { startNodeId }));
  const chooseAnswer = (answerId) => {
    setSession((current) => advancePreview(story, current, answerId));
  };

  const isBroken = ["unfinished", "invalid", "loop"].includes(session.status);

  return (
    <div className="interactive-preview-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose?.();
    }}>
      <section
        className={`interactive-preview ${showDebug ? "has-debug" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="interactive-preview-title"
      >
        <header className="interactive-preview__header">
          <div>
            <span className="interactive-preview__kicker">文字游戏预演</span>
            <h2 id="interactive-preview-title">{story.title || "互动游戏"}</h2>
          </div>
          <div className="interactive-preview__header-actions">
            <Button type="button" size="sm" variant={showDebug ? "secondary" : "ghost"} onClick={() => setShowDebug((value) => !value)}>
              <Bug size={14} /> 状态
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={restart}>
              <RotateCcw size={14} /> 重来
            </Button>
            <IconButton type="button" label="关闭预演" onClick={onClose}>
              <X size={18} />
            </IconButton>
          </div>
        </header>

        <div className="interactive-preview__body">
          <main className="interactive-preview__stage">
            {startNodeId && startNodeId !== story.entryNodeId ? (
              <div className="interactive-preview__debug-start" role="note">
                <CircleAlert size={15} />
                <span>正在从中途节点调试，人物关系与剧情标记使用开场初始状态。</span>
              </div>
            ) : null}
            {session.lastFeedback ? (
              <div className="interactive-preview__feedback" role="status">
                <Check size={15} />
                <span>{session.lastFeedback}</span>
              </div>
            ) : null}

            {isBroken ? (
              <div className="interactive-preview__error">
                <CircleAlert size={30} />
                <h3>这条剧情还没写完</h3>
                <p>{session.error || "暂时无法继续预演"}</p>
                <Button type="button" variant="secondary" onClick={restart}>
                  <RotateCcw size={15} /> 从头重来
                </Button>
              </div>
            ) : currentNode ? (
              <article className={`interactive-preview__scene ${currentNode.nodeType === "ending" ? "is-ending" : ""}`}>
                <div className="interactive-preview__scene-meta">
                  <span><BookOpen size={13} />{chapter?.title || "未分章"}</span>
                  {currentNode.scene ? <span><MapPin size={13} />{currentNode.scene}</span> : null}
                  {participants.length ? <span><Users size={13} />{participants.map((item) => item.name).join("、")}</span> : null}
                </div>

                {currentNode.nodeType === "ending" ? (
                  <span className={`interactive-preview__ending-badge ending-${currentNode.endingType || "normal"}`}>
                    <Flag size={14} />{ENDING_LABELS[currentNode.endingType] || "结局"}
                  </span>
                ) : null}

                <h3>{currentNode.title || "未命名剧情"}</h3>
                <div className="interactive-preview__prose">
                  {currentNode.content?.trim() || "这一段剧情还没有正文。"}
                </div>

                {currentNode.nodeType === "ending" ? (
                  <div className="interactive-preview__ending-actions">
                    <strong>故事在这里结束</strong>
                    <Button type="button" variant="secondary" onClick={restart}>
                      <RotateCcw size={15} /> 重新体验
                    </Button>
                  </div>
                ) : (
                  <div className="interactive-preview__choice-block">
                    <div className="interactive-preview__question">
                      <GitBranch size={18} />
                      <h4>{currentNode.question?.trim() || "接下来要怎么做？"}</h4>
                    </div>
                    <div className="interactive-preview__answers">
                      {availableAnswers.map((answer, index) => {
                        const impact = IMPACT_TYPES[answer.impactType] || IMPACT_TYPES.local;
                        return (
                          <button
                            type="button"
                            key={answer.id}
                            className={`interactive-preview__answer impact-${answer.impactType || "local"}`}
                            onClick={() => chooseAnswer(answer.id)}
                          >
                            <span className="interactive-preview__answer-index">{String(index + 1).padStart(2, "0")}</span>
                            <span className="interactive-preview__answer-copy">
                              <strong>{answer.text || `回答 ${index + 1}`}</strong>
                              <small style={{ color: impact.color }}>{impact.label}</small>
                            </span>
                          </button>
                        );
                      })}
                      {!availableAnswers.length ? (
                        <div className="interactive-preview__no-answers">
                          <CircleAlert size={17} /> 当前状态下没有可选回答
                        </div>
                      ) : null}
                    </div>
                    {showDebug && hiddenAnswerCount > 0 ? (
                      <span className="interactive-preview__hidden-count">有 {hiddenAnswerCount} 个回答因条件不满足而隐藏</span>
                    ) : null}
                  </div>
                )}
              </article>
            ) : null}
          </main>

          {showDebug ? <RuntimeDebug story={story} session={session} /> : null}
        </div>

        <footer className="interactive-preview__footer">
          <span>已走过 {session.visitedNodeIds.length} 段剧情</span>
          <span>{session.history.length} 次选择</span>
        </footer>
      </section>
    </div>
  );
}
